import React, { useState, useEffect, useContext } from 'react';
import classNames from 'classnames/bind';
import styles from './WashManager.module.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes, faEnvelope, faTimesCircle, faSpinner, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { ToastContext } from '~/components/context/ToastContext';
import { ModalContext } from '~/components/context/ModalContext';
import { BASE_DRIVER_URL, BASE_URL } from '~/helper/apiService';

const cx = classNames.bind(styles);

function WashManager() {
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [washHistory, setWashHistory] = useState([]);
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const { showToast } = useContext(ToastContext);
  const { showModal } = useContext(ModalContext);

  // Helper function to translate status to Vietnamese
  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return 'Chờ duyệt';
      case 'approved':
        return 'Đã duyệt';
      case 'rejected':
        return 'Bị từ chối';
      default:
        return 'Không xác định';
    }
  };

  // Helper function to render action buttons
  const renderActionButtons = (report, driverName, isLoading) => {
    const commonProps = {
      disabled: isLoading,
      onClick: () => confirmModerate(report.id, report.status === 'rejected' ? 'approved' : 'rejected', driverName),
    };

    if (report.status === 'pending') {
      return (
        <>
          <button className={cx('action-button', 'approve')} title="Duyệt" {...commonProps}>
            <FontAwesomeIcon icon={faCheck} />
          </button>
          <button className={cx('action-button', 'reject')} title="Từ Chối" {...commonProps}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </>
      );
    }

    return (
      <button
        className={cx('action-button', report.status === 'rejected' ? 'approve' : 'reject')}
        title={report.status === 'rejected' ? 'Duyệt' : 'Từ Chối'}
        {...commonProps}
      >
        <FontAwesomeIcon icon={report.status === 'rejected' ? faCheck : faTimes} />
      </button>
    );
  };

  // Lấy danh sách tài xế và số lần rửa xe còn thiếu
  const fetchDrivers = async () => {
    setLoadingDrivers(true);
    try {
      const response = await fetch(`${BASE_DRIVER_URL}users-wash-status`, {
        credentials: 'include',
      });
      const data = await response.json();
      if (response.ok) {
        setDrivers(data.users || []);
      } else {
        showToast(data.error || 'Lỗi khi lấy danh sách tài xế', 'error');
      }
    } catch (error) {
      showToast('Lỗi kết nối: ' + error.message, 'error');
    } finally {
      setLoadingDrivers(false);
    }
  };

  // Lấy lịch sử rửa xe của tài xế
  const fetchWashHistory = async (vehicleId) => {
    setLoadingHistory(true);
    try {
      const response = await fetch(`${BASE_DRIVER_URL}wash-history/${vehicleId}`, {
        credentials: 'include',
      });
      const data = await response.json();
      if (response.ok) {
        setWashHistory(data.history || []);
      } else {
        showToast(data.message || 'Lỗi khi lấy lịch sử rửa xe', 'error');
      }
    } catch (error) {
      showToast('Lỗi kết nối: ' + error.message, 'error');
    } finally {
      setLoadingHistory(false);
    }
  };

  // Xử lý duyệt hoặc từ chối báo cáo
  const handleModerate = async (reportId, status) => {
    setLoadingHistory(true);
    try {
      const response = await fetch(`${BASE_DRIVER_URL}moderate-image/${reportId}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      const data = await response.json();
      if (response.ok) {
        showToast(data.message, 'success');
        fetchWashHistory(selectedDriver.driver_id); // Cập nhật lịch sử
        fetchDrivers(); // Cập nhật số lần rửa xe còn thiếu
      } else {
        showToast(data.message || 'Lỗi khi duyệt báo cáo', 'error');
      }
    } catch (error) {
      showToast('Lỗi kết nối: ' + error.message, 'error');
    } finally {
      setLoadingHistory(false);
    }
  };

  // Xử lý duyệt tất cả báo cáo rửa xe trong tháng
  const handleModerateMonth = async (driverId, month, driverName) => {
    setLoadingHistory(true);
    try {
      const response = await fetch(`${BASE_DRIVER_URL}moderate-month-washes`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id_driver: driverId, month }),
      });
      const data = await response.json();
      if (response.ok) {
        showToast(data.message, 'success');
        fetchWashHistory(driverId); // Cập nhật lịch sử
        fetchDrivers(); // Cập nhật số lần rửa xe còn thiếu
      } else {
        showToast(data.message || 'Lỗi khi duyệt tất cả báo cáo', 'error');
      }
    } catch (error) {
      showToast('Lỗi kết nối: ' + error.message, 'error');
    } finally {
      setLoadingHistory(false);
    }
  };

  // Xác nhận duyệt hoặc từ chối
  const confirmModerate = (reportId, status, driverName) => {
    showModal({
      title: `Xác Nhận ${status === 'approved' ? 'Duyệt' : 'Từ Chối'} Báo Cáo`,
      message: `Bạn có chắc chắn muốn ${status === 'approved' ? 'duyệt' : 'từ chối'} báo cáo rửa xe này của tài xế ${driverName}?`,
      onConfirm: () => handleModerate(reportId, status),
      onCancel: null,
    });
  };

  // Xác nhận duyệt tất cả báo cáo trong tháng
  const confirmModerateMonth = (driverId, month, driverName) => {
    showModal({
      title: 'Xác Nhận Duyệt Tất Cả Báo Cáo',
      message: `Bạn có chắc chắn muốn duyệt tất cả báo cáo rửa xe trong tháng ${month} của tài xế ${driverName}?`,
      onConfirm: () => handleModerateMonth(driverId, month, driverName),
      onCancel: null,
    });
  };

  // Gửi nhắc nhở rửa xe
  const handleSendReminder = async (driverId, driverName, email) => {
    try {
      const response = await fetch(`${BASE_DRIVER_URL}send-wash-reminder/${driverId}`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await response.json();
      if (response.ok) {
        showToast(data.message, 'success');
      } else {
        showToast(data.message || 'Lỗi khi gửi nhắc nhở', 'error');
      }
    } catch (error) {
      showToast('Lỗi kết nối: ' + error.message, 'error');
    }
  };

  // Xác nhận gửi nhắc nhở
  const confirmSendReminder = (driverId, driverName, email) => {
    showModal({
      title: 'Xác Nhận Gửi Nhắc Nhở',
      message: `Bạn có chắc chắn muốn gửi email nhắc nhở rửa xe tới ${driverName} (${email})?`,
      onConfirm: () => handleSendReminder(driverId, driverName, email),
      onCancel: null,
    });
  };

  // Xử lý chọn tài xế
  const handleSelectDriver = (driver) => {
    if (selectedDriver && selectedDriver.id === driver.id) {
      setSelectedDriver(null);
      setWashHistory([]);
    } else {
      setSelectedDriver(driver);
      fetchWashHistory(driver.driver_id);
    }
  };

  // Xử lý phóng to ảnh
  const handleImageClick = (image) => {
    setSelectedImage(image);
  };

  // Đóng modal ảnh
  const closeImageModal = () => {
    setSelectedImage(null);
  };

  // Xử lý lỗi khi ảnh không tải được
  const handleImageError = (e) => {
    e.target.style.display = 'none';
    const errorSpan = document.createElement('span');
    errorSpan.className = cx('image-error');
    errorSpan.textContent = 'Đã xóa';
    e.target.parentNode.appendChild(errorSpan);
  };

  // Kiểm tra xem tháng có báo cáo nào đang chờ duyệt không
  const hasPendingReports = (reports) => {
    return reports.some(report => report.status === 'pending' || report.status === 'rejected');
  };

  useEffect(() => {
    setSelectedDriver(null); // Đảm bảo không có tài xế nào được chọn
    setWashHistory([]); // Xóa lịch sử
    fetchDrivers();
  }, []);

  const isLoading = loadingDrivers || loadingHistory;

  return (
    <div className={cx('wash-manager')}>
      <h3 className={cx('subtitle')}>Danh Sách Tài Xế</h3>
      {loadingDrivers ? (
        <div className={cx('loading-text')}>
          <FontAwesomeIcon icon={faSpinner} spin />
          <span>Đang tải dữ liệu...</span>
        </div>
      ) : drivers.length === 0 ? (
        <div className={cx('no-data')}>Không có tài xế nào</div>
      ) : (
        <table className={cx('table')}>
          <thead>
            <tr className={cx('tr')}>
              <th className={cx('th')}>Tên</th>
              <th className={cx('th')}>Biển Số Xe</th>
              <th className={cx('th')}>Mẫu Xe</th>
              <th className={cx('th')}>Rửa Xe Thiếu</th>
              <th className={cx('th')}>Hành Động</th>
            </tr>
          </thead>
          <tbody>
            {drivers.map((driver) => (
              <tr
                key={driver.id}
                className={cx('tr', { active: selectedDriver && selectedDriver.id === driver.id })}
                onClick={() => !isLoading && handleSelectDriver(driver)}
              >
                <td className={cx('td')}>{driver.name}</td>
                <td className={cx('td')}>{driver.license_plate}</td>
                <td className={cx('td')}>{driver.model}</td>
                <td className={cx('td', { negative: driver.missing_washes > 0 })}>
                  {driver.missing_washes}
                </td>
                <td className={cx('td')}>
                  <button
                    className={cx('action-button', 'mail')}
                    onClick={(e) => {
                      e.stopPropagation();
                      confirmSendReminder(driver.driver_id, driver.name, driver.email);
                    }}
                    title="Gửi Nhắc Nhở"
                    disabled={isLoading}
                  >
                    <FontAwesomeIcon icon={faEnvelope} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {selectedDriver && (
        <div className={cx('bottom')}>
          <h3 className={cx('subtitle')}>
            Lịch Sử Rửa Xe: {selectedDriver.name}
          </h3>
          {loadingHistory ? (
            <div className={cx('loading-text')}>
              <FontAwesomeIcon icon={faSpinner} spin />
              <span>Đang tải dữ liệu...</span>
            </div>
          ) : washHistory.length === 0 ? (
            <div className={cx('no-data')}>Không có báo cáo rửa xe nào</div>
          ) : (
            washHistory.map((month) => (
              <div key={month.month} className={cx('month-section')}>
                <div className={cx('month-header')}>
                  <h4 className={cx('month-title')}>
                    Tháng: {month.month} | Rửa Xe: {month.washCount}/8 | Thiếu: {month.missingWash}
                  </h4>
                  {hasPendingReports(month.reports) && (
                    <button
                      className={cx('action-button', 'approve-all')}
                      onClick={() => confirmModerateMonth(selectedDriver.driver_id, month.month, selectedDriver.name)}
                      title="Duyệt Tất Cả"
                      disabled={isLoading}
                    >
                      <FontAwesomeIcon icon={faCheckCircle} />
                      <span>Duyệt Tất Cả</span>
                    </button>
                  )}
                </div>
                <table className={cx('table')}>
                  <thead>
                    <tr className={cx('tr')}>
                      <th className={cx('th')}>Ảnh</th>
                      <th className={cx('th')}>Trạng Thái</th>
                      <th className={cx('th')}>Thời Gian</th>
                      <th className={cx('th')}>Hành Động</th>
                    </tr>
                  </thead>
                  <tbody>
                    {month.reports.map((report) => (
                      <tr key={report.id} className={cx('tr')}>
                        <td className={cx('td')}>
                          {report.images.length > 0 ? (
                            report.images.map((img, idx) => (
                              <div key={idx} className={cx('image-container')}>
                                <img
                                  src={`${BASE_URL}${img}`}
                                  alt="Wash"
                                  className={cx('wash-image')}
                                  onClick={() => !isLoading && handleImageClick(img)}
                                  onError={handleImageError}
                                />
                              </div>
                            ))
                          ) : (
                            <span>Không có ảnh</span>
                          )}
                        </td>
                        <td className={cx('td', { [report.status]: true })}>{getStatusText(report.status)}</td>
                        <td className={cx('td')}>
                          {new Date(report.created_at).toLocaleString('vi-VN')}
                        </td>
                        <td className={cx('td')}>
                          <div className={cx('action-btn')}>
                            {renderActionButtons(report, selectedDriver.name, isLoading)}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))
          )}
        </div>
      )}

      {selectedImage && (
        <div className={cx('image-overlay')}>
          <div className={cx('image-modal')}>
            <button className={cx('close-button')} onClick={closeImageModal} disabled={isLoading}>
              <FontAwesomeIcon icon={faTimesCircle} />
            </button>
            <img
              src={`${BASE_URL}${selectedImage}`}
              alt="Full-size wash"
              className={cx('full-image')}
              onError={handleImageError}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default WashManager;